import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, Loader2, Search, Plus, Edit, Trash2, Download, Eye,
  X, Upload, Filter, Book, Scale, Gavel, FileCheck
} from 'lucide-react';
import { adminRegulationsAPI } from '../services/api';
import Pagination from '../components/Pagination';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const categoryIcons = {
  regulation: <Book size={20} />,
  decree: <Gavel size={20} />,
  law: <Scale size={20} />,
  order: <FileCheck size={20} />,
  other: <FileText size={20} />
};

const categoryColors = {
  regulation: '#3b82f6',
  decree: '#10b981',
  law: '#8b5cf6',
  order: '#f59e0b',
  other: '#6b7280'
};

const RegulationsPage = () => {
  const [regulations, setRegulations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRegulation, setEditingRegulation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'regulation',
    file: null
  });
  
  const fileRef = useRef(null);

  useEffect(() => {
    fetchRegulations();
  }, [filterCategory]);

  const fetchRegulations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (searchTerm) params.search = searchTerm;
      
      const response = await adminRegulationsAPI.getAll(params);
      setRegulations(response.data.regulations || []);
      setCategories(response.data.categories || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRegulations();
  };

  const openModal = (regulation = null) => {
    if (regulation) {
      setEditingRegulation(regulation);
      setFormData({
        title: regulation.title,
        description: regulation.description || '',
        category: regulation.category,
        file: null
      });
    } else {
      setEditingRegulation(null);
      setFormData({
        title: '',
        description: '',
        category: 'regulation',
        file: null
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRegulation(null);
    setFormData({ title: '', description: '', category: 'regulation', file: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('category', formData.category);
      if (formData.file) {
        data.append('file', formData.file);
      }
      
      if (editingRegulation) {
        await adminRegulationsAPI.update(editingRegulation.id, data);
      } else {
        await adminRegulationsAPI.create(data);
      }
      
      closeModal();
      fetchRegulations();
    } catch (error) {
      alert('Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Nizomni o'chirmoqchimisiz?")) {
      try {
        await adminRegulationsAPI.delete(id);
        fetchRegulations();
      } catch (error) {
        alert('Xatolik yuz berdi');
      }
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('uz-UZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Scale size={28} /> Nizom va qarorlar</h1>
          <p>Hukumat qarorlari va ichki nizomlar</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <Plus size={18} />
          Yangi qo'shish
        </button>
      </header>

      {/* Statistika */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total"><FileText size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Jami hujjatlar</span>
            </div>
          </div>
          {Object.entries(stats.by_category || {}).map(([key, data]) => (
            <div className="stat-card" key={key}>
              <div className="stat-icon" style={{ background: `${categoryColors[key]}20`, color: categoryColors[key] }}>
                {categoryIcons[key]}
              </div>
              <div className="stat-info">
                <span className="stat-value">{data.count}</span>
                <span className="stat-label">{data.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filterlar */}
      <div className="filters-row">
        <form onSubmit={handleSearch} className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>
        <select 
          className="filter-select"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="">Barcha turlar</option>
          {categories.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Jadval */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : regulations.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>Hujjatlar topilmadi</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sarlavha</th>
                <th>Turi</th>
                <th>Fayl</th>
                <th>Sana</th>
                <th>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {regulations.map(reg => (
                <tr key={reg.id}>
                  <td>
                    <div className="regulation-title">
                      <strong>{reg.title}</strong>
                      {reg.description && (
                        <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
                          {reg.description.substring(0, 100)}{reg.description.length > 100 ? '...' : ''}
                        </p>
                      )}
                    </div>
                  </td>
                  <td>
                    <span 
                      className="category-badge"
                      style={{ 
                        background: `${categoryColors[reg.category]}15`,
                        color: categoryColors[reg.category],
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {categoryIcons[reg.category]}
                      {reg.category_display}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: '#64748b', fontSize: '13px' }}>
                      {formatFileSize(reg.file_size)}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: '#64748b', fontSize: '13px' }}>
                      {formatDate(reg.created_at)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <a 
                        href={getFileUrl(reg.file_url)} 
                        download
                        className="btn btn-icon btn-info"
                        title="Yuklab olish"
                      >
                        <Download size={18} />
                      </a>
                      <a 
                        href={getFileUrl(reg.file_url)} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-icon"
                        style={{ background: '#f1f5f9', color: '#475569' }}
                        title="Ko'rish"
                      >
                        <Eye size={18} />
                      </a>
                      <button 
                        className="btn btn-icon btn-warning"
                        onClick={() => openModal(reg)}
                        title="Tahrirlash"
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        className="btn btn-icon btn-danger"
                        onClick={() => handleDelete(reg.id)}
                        title="O'chirish"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRegulation ? "Nizomni tahrirlash" : "Yangi nizom qo'shish"}</h2>
              <button className="close-btn" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Sarlavha *</label>
                  <input 
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Nizom yoki qaror sarlavhasi"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Izoh</label>
                  <textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Qisqacha tavsif..."
                    rows={3}
                  />
                </div>
                
                <div className="form-group">
                  <label>Turi *</label>
                  <select 
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    {categories.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Fayl {!editingRegulation && '*'}</label>
                  <input 
                    type="file"
                    ref={fileRef}
                    onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                    accept=".pdf,.doc,.docx"
                    required={!editingRegulation}
                    style={{ display: 'none' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => fileRef.current?.click()}
                    >
                      <Upload size={18} />
                      Fayl tanlash
                    </button>
                    {formData.file && (
                      <span style={{ color: '#10b981', fontSize: '14px' }}>
                        {formData.file.name}
                      </span>
                    )}
                    {editingRegulation && !formData.file && (
                      <span style={{ color: '#64748b', fontSize: '14px' }}>
                        Mavjud fayl saqlanadi
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <Loader2 className="spinner" size={18} /> : null}
                  {editingRegulation ? "Saqlash" : "Qo'shish"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegulationsPage;
